/**
 * Consultation Hero — Multi-step booking form
 * Steps: Date → Time → Your Info → Review → Confirmation
 */

class ConsultationForm extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 1;
    this.totalSteps = 5;
    this.state = {
      date: null,
      time: null,
      name: '',
      email: '',
      phone: '',
      space: ''
    };

    this.today = new Date();
    this.today.setHours(0, 0, 0, 0);
    this.viewMonth = this.today.getMonth();
    this.viewYear = this.today.getFullYear();
  }

  connectedCallback() {
    this.cacheDOM();
    this.readSettings();
    this.renderCalendar();
    this.bindEvents();
    if (this.apiBaseUrl) {
      this.fetchAvailability();
    }
  }

  cacheDOM() {
    this.panels = this.querySelectorAll('[data-step]');
    this.stepIndicators = this.querySelectorAll('[data-step-indicator]');
    this.lineFill = this.querySelector('.consultation-card__step-line-fill');
    this.calendarGrid = this.querySelector('[data-calendar-grid]');
    this.calendarTitle = this.querySelector('[data-calendar-title]');
    this.timeGrid = this.querySelector('[data-time-grid]');
    this.form = this.querySelector('[data-consultation-form]');
  }

  readSettings() {
    const section = this.closest('section');
    this.apiBaseUrl = (section?.dataset?.apiUrl || '').replace(/\/$/, '');
    const disabledAttr = section?.dataset?.disabledTimes || '';
    this.disabledTimes = disabledAttr
      ? disabledAttr.split(',').map(t => t.trim())
      : ['11:00 AM', '3:00 PM'];
    this.minDaysAhead = parseInt(section?.dataset?.minDaysAhead || '1', 10);
    this.disableWeekends = section?.dataset?.disableWeekends === 'true';
    this.formEndpoint = section?.dataset?.formEndpoint || '';

    this.availabilityByDate = {};
    this.availabilityFetched = null;

    if (!this.apiBaseUrl && this.timeGrid) {
      const slots = this.timeGrid.querySelectorAll('.consultation-time-slot');
      slots.forEach(slot => {
        if (this.disabledTimes.includes(slot.dataset.time)) {
          slot.disabled = true;
        }
      });
    }
  }

  bindEvents() {
    this.querySelectorAll('[data-next-step]').forEach(btn => {
      btn.addEventListener('click', () => this.goToStep(this.currentStep + 1));
    });

    this.querySelectorAll('[data-prev-step]').forEach(btn => {
      btn.addEventListener('click', () => this.goToStep(this.currentStep - 1));
    });

    this.querySelectorAll('[data-goto-step]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.goToStep(parseInt(btn.dataset.gotoStep, 10));
      });
    });

    this.querySelector('[data-calendar-prev]')?.addEventListener('click', () => {
      this.viewMonth--;
      if (this.viewMonth < 0) { this.viewMonth = 11; this.viewYear--; }
      this.availabilityFetched = null;
      this.renderCalendar();
      if (this.apiBaseUrl) this.fetchAvailability();
    });

    this.querySelector('[data-calendar-next]')?.addEventListener('click', () => {
      this.viewMonth++;
      if (this.viewMonth > 11) { this.viewMonth = 0; this.viewYear++; }
      this.availabilityFetched = null;
      this.renderCalendar();
      if (this.apiBaseUrl) this.fetchAvailability();
    });

    this.timeGrid?.addEventListener('click', (e) => {
      const slot = e.target.closest('.consultation-time-slot');
      if (!slot || slot.disabled) return;
      this.timeGrid.querySelectorAll('.consultation-time-slot').forEach(s => s.classList.remove('is-selected'));
      slot.classList.add('is-selected');
      this.state.time = slot.dataset.time;
      this.updateContinueState(2);
    });

    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (this.validateForm()) {
          this.collectFormData();
          this.goToStep(4);
        }
      });
    }

    this.querySelector('[data-confirm-booking]')?.addEventListener('click', () => {
      this.submitBooking();
    });

    document.querySelectorAll('.js-scroll-to-form')?.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const target = document.getElementById('consultation-booking');
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  }

  goToStep(step) {
    if (step < 1 || step > this.totalSteps) return;
    this.currentStep = step;

    this.panels.forEach(p => p.classList.remove('is-active'));
    const activePanel = this.querySelector(`[data-step="${step}"]`);
    if (activePanel) activePanel.classList.add('is-active');

    this.stepIndicators.forEach(ind => {
      const s = parseInt(ind.dataset.stepIndicator, 10);
      ind.classList.remove('is-active', 'is-complete');
      if (s === step || (step === 5 && s === 4)) ind.classList.add('is-active');
      if (s < step || step === 5) ind.classList.add('is-complete');
    });

    const progress = step >= 5 ? 1 : (step - 1) / 3;
    if (this.lineFill) {
      this.lineFill.style.transform = `scaleX(${progress})`;
    }

    if (step === 2) {
      this.populateDateLabel();
      this.applyTimeSlotsForDate();
    }
    if (step === 4) this.populateReview();
  }

  applyTimeSlotsForDate() {
    if (!this.timeGrid) return;
    const slots = this.timeGrid.querySelectorAll('.consultation-time-slot');
    const dateStr = this.state.date ? this.dateStr(this.state.date) : '';
    const monthKey = this.state.date ? `${this.state.date.getFullYear()}-${String(this.state.date.getMonth() + 1).padStart(2, '0')}` : '';
    const hasApiDataForDate = this.apiBaseUrl && this.availabilityFetched === monthKey && dateStr && Array.isArray(this.availabilityByDate[dateStr]) && this.availabilityByDate[dateStr].length > 0;

    if (hasApiDataForDate) {
      const available = this.availabilityByDate[dateStr];
      const normalize = (t) => (t || '').trim().toUpperCase();
      slots.forEach(slot => {
        const slotTime = normalize(slot.dataset.time);
        const enabled = Array.isArray(available) && available.some((t) => normalize(t) === slotTime);
        slot.disabled = !enabled;
        slot.classList.toggle('is-available', enabled);
      });
    } else {
      slots.forEach(slot => {
        slot.disabled = this.disabledTimes.includes(slot.dataset.time);
        slot.classList.toggle('is-available', !this.disabledTimes.includes(slot.dataset.time));
      });
    }
  }

  /* ---- API availability ---- */
  monthKey() {
    const m = this.viewMonth + 1;
    return `${this.viewYear}-${String(m).padStart(2, '0')}`;
  }

  async fetchAvailability() {
    if (!this.apiBaseUrl) return;
    const key = this.monthKey();
    if (this.availabilityFetched === key) return;
    try {
      const res = await fetch(`${this.apiBaseUrl}/availability?month=${key}`);
      if (!res.ok) {
        this.availabilityByDate = {};
        this.renderCalendar();
        return;
      }
      const data = await res.json();
      this.availabilityByDate = data || {};
      this.availabilityFetched = key;
      this.renderCalendar();
    } catch (e) {
      this.availabilityByDate = {};
      this.renderCalendar();
    }
  }

  dateStr(date) {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /* ---- Calendar ---- */
  renderCalendar() {
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    if (this.calendarTitle) {
      this.calendarTitle.textContent = `${months[this.viewMonth]} ${this.viewYear}`;
    }

    if (!this.calendarGrid) return;
    this.calendarGrid.innerHTML = '';

    const firstDay = new Date(this.viewYear, this.viewMonth, 1).getDay();
    const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();

    const minDate = new Date(this.today);
    minDate.setDate(minDate.getDate() + this.minDaysAhead);

    for (let i = 0; i < firstDay; i++) {
      const empty = document.createElement('div');
      empty.className = 'consultation-calendar__day consultation-calendar__day--empty';
      this.calendarGrid.appendChild(empty);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'consultation-calendar__day';
      btn.textContent = d;

      const date = new Date(this.viewYear, this.viewMonth, d);
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isPast = date < minDate;
      const dateStr = this.dateStr(date);
      const noSlots = this.apiBaseUrl && this.availabilityFetched === this.monthKey() && (!this.availabilityByDate[dateStr] || this.availabilityByDate[dateStr].length === 0);

      if (isWeekend) btn.classList.add('is-weekend');
      if (isPast || (this.disableWeekends && isWeekend) || noSlots) {
        btn.disabled = true;
      }

      if (this.state.date &&
          date.getFullYear() === this.state.date.getFullYear() &&
          date.getMonth() === this.state.date.getMonth() &&
          date.getDate() === this.state.date.getDate()) {
        btn.classList.add('is-selected');
      }

      btn.addEventListener('click', () => {
        this.calendarGrid.querySelectorAll('.consultation-calendar__day').forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this.state.date = new Date(this.viewYear, this.viewMonth, d);
        this.updateContinueState(1);
      });

      this.calendarGrid.appendChild(btn);
    }
  }

  /* ---- Helpers ---- */
  updateContinueState(step) {
    const panel = this.querySelector(`[data-step="${step}"]`);
    if (!panel) return;
    const btn = panel.querySelector('[data-next-step]');
    if (!btn) return;

    if (step === 1 && this.state.date) btn.disabled = false;
    if (step === 2 && this.state.time) btn.disabled = false;
  }

  populateDateLabel() {
    const label = this.querySelector('[data-selected-date-label]');
    if (label && this.state.date) {
      const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
      const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      label.textContent = `${days[this.state.date.getDay()]}, ${months[this.state.date.getMonth()]} ${this.state.date.getDate()}`;
    }
  }

  validateForm() {
    let valid = true;
    const name = this.form.querySelector('#consult-name');
    const email = this.form.querySelector('#consult-email');

    [name, email].forEach(input => input.classList.remove('is-invalid'));

    if (!name.value.trim()) {
      name.classList.add('is-invalid');
      valid = false;
    }

    if (!email.value.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) {
      email.classList.add('is-invalid');
      valid = false;
    }

    return valid;
  }

  collectFormData() {
    this.state.name = this.form.querySelector('#consult-name').value.trim();
    this.state.email = this.form.querySelector('#consult-email').value.trim();
    this.state.phone = this.form.querySelector('#consult-phone').value.trim();
    this.state.space = this.form.querySelector('#consult-space').value.trim();
  }

  populateReview() {
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

    if (this.state.date) {
      const d = this.state.date;
      const dateStr = `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
      const el = this.querySelector('[data-review-date]');
      if (el) el.textContent = dateStr;
    }

    const timeEl = this.querySelector('[data-review-time]');
    if (timeEl) timeEl.textContent = this.state.time || '';

    const nameEl = this.querySelector('[data-review-name]');
    if (nameEl) nameEl.textContent = this.state.name;

    const emailEl = this.querySelector('[data-review-email]');
    if (emailEl) emailEl.textContent = this.state.email;

    const phoneEl = this.querySelector('[data-review-phone]');
    if (phoneEl) phoneEl.textContent = this.state.phone || '—';

    const spaceEl = this.querySelector('[data-review-space]');
    if (spaceEl) spaceEl.textContent = this.state.space || '—';
  }

  async submitBooking() {
    const confirmBtn = this.querySelector('[data-confirm-booking]');
    if (confirmBtn) {
      confirmBtn.disabled = true;
      confirmBtn.innerHTML = 'Booking&hellip;';
    }

    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

    const payload = {
      date: this.state.date ? this.dateStr(this.state.date) : '',
      time: this.state.time,
      name: this.state.name,
      email: this.state.email,
      phone: this.state.phone || '',
      space: this.state.space || ''
    };

    let success = false;
    let errorMessage = '';

    try {
      if (this.apiBaseUrl) {
        const res = await fetch(`${this.apiBaseUrl}/book`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.success) {
          success = true;
          const el = this.querySelector('[data-confirm-meet]');
          if (el) {
            el.href = (data.calendarAddUrl || data.meetLink || '#');
            el.textContent = 'Save in your calendar';
            el.style.display = data.calendarAddUrl || data.meetLink ? '' : 'none';
          }
        } else {
          errorMessage = data.error || (res.status === 409 ? 'This slot was just taken. Please choose another time.' : 'Booking failed. Please try again.');
        }
      } else if (this.formEndpoint) {
        await fetch(this.formEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        success = true;
      } else {
        const formData = new FormData();
        formData.append('form_type', 'contact');
        formData.append('utf8', '✓');
        formData.append('contact[consultation_date]', payload.date);
        formData.append('contact[consultation_time]', payload.time);
        formData.append('contact[name]', payload.name);
        formData.append('contact[email]', payload.email);
        formData.append('contact[phone]', payload.phone);
        formData.append('contact[body]', `Consultation Request\nDate: ${payload.date}\nTime: ${payload.time}\nSpace: ${payload.space}`);

        await fetch('/contact#contact_form', {
          method: 'POST',
          body: formData
        });
        success = true;
      }
    } catch (err) {
      console.error('Booking submission error:', err);
      errorMessage = 'Booking failed. Please try again.';
    }

    if (!success && errorMessage && confirmBtn) {
      confirmBtn.disabled = false;
      confirmBtn.innerHTML = 'Confirm Booking <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
      let errEl = this.querySelector('[data-booking-error]');
      if (!errEl) {
        errEl = document.createElement('p');
        errEl.setAttribute('data-booking-error');
        errEl.className = 'consultation-card__error';
        errEl.setAttribute('role', 'alert');
        this.querySelector('[data-step="4"]')?.appendChild(errEl);
      }
      errEl.textContent = errorMessage;
      errEl.style.display = 'block';
      return;
    }

    if (success) {
      const eventId = 'consult_' + Date.now();
      if (typeof fbq === 'function') {
        fbq('track', 'Lead', {
          content_name: 'Design Consultation',
          content_category: 'Consultation',
          value: 0,
          currency: 'USD'
        }, { eventID: eventId });
      }
      if (typeof gtag === 'function') {
        gtag('event', 'generate_lead', {
          event_category: 'Consultation',
          event_label: 'Design Consultation Booking',
          value: 0,
          currency: 'USD'
        });
      }
      if (typeof dataLayer !== 'undefined' && Array.isArray(dataLayer)) {
        dataLayer.push({
          event: 'consultation_booking_success',
          consultation_date: payload.date,
          consultation_time: payload.time
        });
      }
    }

    const d = this.state.date;
    if (d) {
      const dtEl = this.querySelector('[data-confirm-datetime]');
      if (dtEl) {
        dtEl.textContent = `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()} at ${this.state.time}`;
      }
    }

    this.goToStep(5);
  }
}

if (!customElements.get('consultation-form')) {
  customElements.define('consultation-form', ConsultationForm);
}
